import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Chip,
  Divider,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
// import useElementSize from '@/hooks/useElementSize.ts';
import dataCheck from "./drug-list.json";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

type DrugCategory =
  | "Gây nghiện"
  | "Hướng thần"
  | "Tiền chất"
  | "Thuốc độc"
  | "Cấm sử dụng";

type ClassificationResult =
  | "Cần check Hoạt chất"
  | "Thuốc cấm sử dụng"
  | "Thuốc độc"
  | "Thuốc gây nghiện"
  | "Cần check gây nghiện"
  | "Thuốc hướng thần"
  | "Cần check hướng thần"
  | "Thuốc tiền chất"
  | "Cần check tiền chất";

type RowValue = string | number | boolean | null | undefined;

type DetectedRow = {
  sku: string;
  name: string;
  groupName: string;
  activeIngredient: string;
  description: string;
  classification: ClassificationResult;
};

type ManualClassificationSummary = {
  classification: ClassificationResult | null;
  activeIngredients: string[];
  matchedSpecialIngredients: Array<{
    name: string;
    category: Extract<DrugCategory, "Gây nghiện" | "Hướng thần" | "Tiền chất">;
  }>;
};

type ReferenceTableRow = {
  internationalName: string;
  group: string;
  dosagePerUnit: string;
  concentration: string;
};

type GoogleSheetCell = {
  v?: RowValue;
} | null;

type GoogleSheetRow = {
  c: GoogleSheetCell[];
};

type GoogleSheetColumn = {
  label?: string | null;
};

type GoogleSheetResponse = {
  table: {
    cols: GoogleSheetColumn[];
    rows: GoogleSheetRow[];
  };
};

// =========================
// 🔥 COLUMNS
// =========================
// const columns = [
//   {
//     title: 'SKU',
//     dataIndex: 'sku',
//     key: 'sku',
//   },
//   {
//     title: 'Tên sản phẩm',
//     dataIndex: 'name',
//     key: 'name',
//   },
//   {
//     title: 'Tên nhóm hàng hóa',
//     dataIndex: 'groupName',
//     key: 'groupName',
//     render: (text: string) => text || '-',
//   },
//   {
//     title: 'Hoạt chất',
//     dataIndex: 'activeIngredient',
//     key: 'activeIngredient',
//     render: (text: string) => (
//       <span className='text-blue-600 font-medium'>{text || '-'}</span>
//     ),
//   },

//   {
//     title: 'Phân loại',
//     dataIndex: 'classification',
//     key: 'classification',
//     render: (value: string) => (
//       <span className='font-semibold text-red-600'>{value}</span>
//     ),
//   },
//   {
//     title: 'Mô tả',
//     dataIndex: 'description',
//     key: 'description',
//     render: (text: string) => (
//       <div className='max-w-[400px] whitespace-pre-wrap'>{text}</div>
//     ),
//   },
// ];

const narcoticCheckColumns = [
  {
    title: "Tên quốc tế",
    dataIndex: "internationalName",
    key: "internationalName",
    width: 220,
  },
  {
    title: "Nhóm",
    dataIndex: "group",
    key: "group",
    width: 180,
  },
  {
    title: "Hàm lượng dược chất theo dạng bazơ trong một đơn vị đã chia liều",
    dataIndex: "dosagePerUnit",
    key: "dosagePerUnit",
    width: 360,
  },
  {
    title: "Nồng độ dược chất theo dạng bazơ trong sản phẩm chưa chia liều",
    dataIndex: "concentration",
    key: "concentration",
    width: 320,
  },
];

const precursorCheckColumns = [
  {
    title: "Tên quốc tế",
    dataIndex: "internationalName",
    key: "internationalName",
    width: 220,
  },
  {
    title: "Nhóm",
    dataIndex: "group",
    key: "group",
    width: 180,
  },
  {
    title: "Hàm lượng tối đa có trong một đơn vị đã chia liều (mg)",
    dataIndex: "dosagePerUnit",
    key: "dosagePerUnit",
    width: 360,
  },
  {
    title: "Nồng độ tối đa có trong một đơn vị chưa chia liều",
    dataIndex: "concentration",
    key: "concentration",
    width: 320,
  },
];

const narcoticCheckReferenceRows: ReferenceTableRow[] = [
  {
    internationalName: "ACETYLDIHYDROCODEIN",
    group: "Phối hợp gây nghiện",
    dosagePerUnit: "100",
    concentration: "2,5",
  },
  {
    internationalName: "COCAINE",
    group: "Phối hợp gây nghiện",
    dosagePerUnit: "",
    concentration: "0,1",
  },
  {
    internationalName: "CODEINE",
    group: "Phối hợp gây nghiện",
    dosagePerUnit: "100",
    concentration: "2,5",
  },
  {
    internationalName: "DIFENOXIN",
    group: "Phối hợp gây nghiện",
    dosagePerUnit:
      "Không quá 0,5 mg Difenoxin và với ít nhất 0,025 mg Atropin Sulfat trong một đơn vị sản phẩm đã chia liều.",
    concentration: "",
  },
  {
    internationalName: "DIPHENOXYLATE",
    group: "Phối hợp gây nghiện",
    dosagePerUnit:
      "Không quá 2,5 mg Difenoxylat và với ít nhất 0,025 mg Atropin Sulfat trong một đơn vị sản phẩm đã chia liều.",
    concentration: "",
  },
  {
    internationalName: "DIHYDROCODEIN",
    group: "Phối hợp gây nghiện",
    dosagePerUnit: "100",
    concentration: "2,5",
  },
  {
    internationalName: "ETHYL MORPHIN",
    group: "Phối hợp gây nghiện",
    dosagePerUnit: "100",
    concentration: "2,5",
  },
  {
    internationalName: "MORPHINE",
    group: "Phối hợp gây nghiện",
    dosagePerUnit: "100",
    concentration: "2,5",
  },
  {
    internationalName: "NICODICODINE",
    group: "Phối hợp gây nghiện",
    dosagePerUnit: "100",
    concentration: "2,5",
  },
  {
    internationalName: "NORCODEINE",
    group: "Phối hợp gây nghiện",
    dosagePerUnit: "100",
    concentration: "2,5",
  },
  {
    internationalName: "PHOLCODIN",
    group: "Phối hợp gây nghiện",
    dosagePerUnit: "100",
    concentration: "2,5",
  },
  {
    internationalName: "PROPIRAM",
    group: "Phối hợp gây nghiện",
    dosagePerUnit: "",
    concentration: "0,2 morphin tính theo morphin base tinh khiết",
  },
  {
    internationalName: "TRAMADOL",
    group: "Phối hợp gây nghiện",
    dosagePerUnit: "37,5",
    concentration: "",
  },
];

const precursorCheckReferenceRows: ReferenceTableRow[] = [
  {
    internationalName: "EPHEDRINE",
    group: "Phối hợp tiền chất",
    dosagePerUnit: "50",
    concentration: "1,5%",
  },
  {
    internationalName: "ERGOMETRINE",
    group: "Phối hợp tiền chất",
    dosagePerUnit: "0,125",
    concentration: "",
  },
  {
    internationalName: "N-ETHYLEPHEDRIN",
    group: "Phối hợp tiền chất",
    dosagePerUnit: "12,5",
    concentration: "",
  },
  {
    internationalName: "N-METHYLEPHEDRIN",
    group: "Phối hợp tiền chất",
    dosagePerUnit: "31,1",
    concentration: "0,0625%",
  },
  {
    internationalName: "ERGOTAMINE",
    group: "Phối hợp tiền chất",
    dosagePerUnit: "1",
    concentration: "",
  },
  {
    internationalName: "PSEUDOEPHEDRINE",
    group: "Phối hợp tiền chất",
    dosagePerUnit: "120",
    concentration: "0,5%",
  },
];

// =========================
// 🔥 TYPES
// =========================
type RowData = {
  [key: string]: RowValue | ClassificationResult;
  classification?: ClassificationResult | null;
};

// =========================
// 🔥 NORMALIZE
// =========================
// const ACTIVE_SEPARATOR_REGEX = /\s*(?:,|\+|\/|&|\||\r?\n|\bVA\b|\bAND\b)\s*/;

const normalizeLabel = (str: string) =>
  str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();

const normalizeCompact = (str: string) =>
  normalizeLabel(str).replace(/[^A-Z0-9]/g, "");

const getCompactVariants = (str: string): string[] => {
  const variants = new Set<string>();
  const addVariant = (value: string) => {
    const compact = normalizeCompact(value);

    if (compact) {
      variants.add(compact);
    }
  };

  addVariant(str);
  addVariant(str.replace(/\([^)]*\)/g, " "));

  return Array.from(variants);
};

const isTruncatedVariantMatch = (left: string, right: string): boolean => {
  if (left === right) return true;

  const [shorter, longer] =
    left.length <= right.length ? [left, right] : [right, left];
  const missingChars = longer.length - shorter.length;

  if (shorter.length < 5 || missingChars < 1 || missingChars > 2) {
    return false;
  }

  return longer.startsWith(shorter);
};

const toText = (value: RowValue | ClassificationResult): string => {
  if (typeof value === "string") return value;
  if (value == null) return "";
  return String(value);
};

const getRowText = (row: RowData, labels: string[]): string => {
  for (const label of labels) {
    const value = row[label];

    if (value != null && value !== "") {
      return toText(value);
    }
  }

  const normalizedLabels = new Set(labels.map(normalizeLabel));

  for (const [key, value] of Object.entries(row)) {
    if (
      normalizedLabels.has(normalizeLabel(key)) &&
      value != null &&
      value !== ""
    ) {
      return toText(value as RowValue | ClassificationResult);
    }
  }

  return "";
};

const parseGoogleSheetResponse = (text: string): GoogleSheetResponse => {
  const match = text.match(
    /google\.visualization\.Query\.setResponse\(([\s\S]+)\);?\s*$/,
  );

  if (!match) {
    throw new Error("Unexpected Google Sheet response format");
  }

  return JSON.parse(match[1]) as GoogleSheetResponse;
};

// =========================
// 🔥 PARSE HOẠT CHẤT
// =========================
const getNormalizedActiveList = (raw: string): string[] => {
  if (!raw) return [];

  // Bước 1: Tách theo các dấu phân cách chính (dấu phẩy, dấu cộng, &, v.v.)
  // Lưu ý: REGEX này CHƯA bao gồm dấu cách đơn để giữ nguyên cụm "Codein Phosphat"
  const MAIN_SEPARATOR = /\s*(?:,|\+|\/|&|\||;|\r?\n|\bVA\b|\bAND\b)\s*/;

  const mainGroups = normalizeLabel(raw)
    .split(MAIN_SEPARATOR)
    .map((s) => s.trim())
    .filter(Boolean);

  const finalResult: string[] = [];

  mainGroups.forEach((group) => {
    // Thêm cụm đầy đủ vào mảng (ví dụ: "Codein Phosphat")
    finalResult.push(group);

    // Bước 2: Nếu cụm có chứa khoảng trắng (nhiều từ), tách tiếp thành từ đơn
    const subWords = group.split(/\s+/).filter(Boolean);

    if (subWords.length > 1) {
      // Thêm từng từ đơn vào mảng (ví dụ: "Codein", "Phosphat")
      finalResult.push(...subWords);
    }
  });

  // Bước 3: Loại bỏ trùng lặp nếu một từ xuất hiện nhiều lần
  return Array.from(new Set(finalResult));
};

const getActiveList = (desc: string): string[] => {
  const raw = getActiveIngredientText(desc);

  if (!raw) return [];

  // Bước 1: Tách theo các dấu phân cách chính (dấu phẩy, dấu cộng, &, v.v.)
  // Lưu ý: REGEX này CHƯA bao gồm dấu cách đơn để giữ nguyên cụm "Codein Phosphat"
  const MAIN_SEPARATOR = /\s*(?:,|\+|\/|&|\||;|\r?\n|\bVA\b|\bAND\b)\s*/;

  const mainGroups = normalizeLabel(raw)
    .split(MAIN_SEPARATOR)
    .map((s) => s.trim())
    .filter(Boolean);

  const finalResult: string[] = [];

  mainGroups.forEach((group) => {
    // Thêm cụm đầy đủ vào mảng (ví dụ: "Codein Phosphat")
    finalResult.push(group);

    // Bước 2: Nếu cụm có chứa khoảng trắng (nhiều từ), tách tiếp thành từ đơn
    const subWords = group.split(/\s+/).filter(Boolean);

    if (subWords.length > 1) {
      // Thêm từng từ đơn vào mảng (ví dụ: "Codein", "Phosphat")
      finalResult.push(...subWords);
    }
  });

  // Bước 3: Loại bỏ trùng lặp nếu một từ xuất hiện nhiều lần
  return Array.from(new Set(finalResult));
};

const getActiveIngredientText = (desc: string): string => {
  const match = desc.match(/Hoạt chất:\s*([^;\r\n]+)/i);
  return match ? match[1].trim() : "";
};

// =========================
// 🔥 BUILD SET
// =========================
const NGHIEN = new Set<string>();
const HUONG_THAN = new Set<string>();
const TIEN_CHAT = new Set<string>();
const DOC = new Set<string>();
const CAM = new Set<string>();
const SPECIAL_ENTRIES: Array<{ key: string; category: DrugCategory }> = [];

Object.entries(dataCheck).forEach(([key, value]) => {
  const k = normalizeCompact(key);

  if (!k) return;

  SPECIAL_ENTRIES.push({ key: k, category: value as DrugCategory });

  if (value === "Gây nghiện") NGHIEN.add(k);
  else if (value === "Hướng thần") HUONG_THAN.add(k);
  else if (value === "Tiền chất") TIEN_CHAT.add(k);
  else if (value === "Thuốc độc") DOC.add(k);
  else if (value === "Cấm sử dụng") CAM.add(k);
});

SPECIAL_ENTRIES.sort((left, right) => right.key.length - left.key.length);

// =========================
// 🔥 HELPERS
// =========================
const findCategoryForSegment = (segment: string): DrugCategory | null => {
  const segmentVariants = getCompactVariants(segment);

  if (!segmentVariants.length) return null;

  const exactMatch = SPECIAL_ENTRIES.find((entry) =>
    segmentVariants.includes(entry.key),
  );

  if (exactMatch) {
    return exactMatch.category;
  }

  const truncatedMatches = SPECIAL_ENTRIES.filter((entry) =>
    segmentVariants.some((variant) =>
      isTruncatedVariantMatch(variant, entry.key),
    ),
  );

  if (truncatedMatches.length === 1) {
    return truncatedMatches[0].category;
  }

  const categories = new Set(truncatedMatches.map((entry) => entry.category));

  if (categories.size === 1 && truncatedMatches.length > 0) {
    return truncatedMatches[0].category;
  }

  return null;
};

const isHighlightedDrugCategory = (
  category: DrugCategory | null,
): category is Extract<
  DrugCategory,
  "Gây nghiện" | "Hướng thần" | "Tiền chất"
> =>
  category === "Gây nghiện" ||
  category === "Hướng thần" ||
  category === "Tiền chất";

const hasAnyCategory = (
  list: Array<DrugCategory | null>,
  category: DrugCategory,
) => list.some((item) => item === category);

const isAllCategory = (
  list: Array<DrugCategory | null>,
  category: DrugCategory,
) => list.length > 0 && list.every((item) => item === category);

const hasNormal = (list: Array<DrugCategory | null>) =>
  list.some((item) => item === null);

const hasSpecialGroup = (groupName: string): boolean => {
  if (!groupName) return false;

  const normalized = groupName.toUpperCase();

  return (
    normalized.includes("TKD") ||
    normalized.includes("TKĐ") ||
    normalized.includes("TKKD") ||
    normalized.includes("TKKĐ") ||
    normalized.includes("TĐD") ||
    normalized.includes("Thuốc")
  );
};

// =========================
// 🚀 DETECT
// =========================
const detectFromActiveIngredients = (
  activeList: string[],
  groupName = "",
): ClassificationResult | null => {
  const matchedCategories = activeList.map(findCategoryForSegment);

  if (!activeList.length && hasSpecialGroup(groupName)) {
    return "Cần check Hoạt chất";
  }

  if (!activeList.length) return null;

  const hasNghien = hasAnyCategory(matchedCategories, "Gây nghiện");
  const hasHuongThan = hasAnyCategory(matchedCategories, "Hướng thần");
  const hasTienChat = hasAnyCategory(matchedCategories, "Tiền chất");
  const hasDoc = hasAnyCategory(matchedCategories, "Thuốc độc");
  const hasCam = hasAnyCategory(matchedCategories, "Cấm sử dụng");
  const hasUnknownActive = hasNormal(matchedCategories);

  if (hasCam) return "Thuốc cấm sử dụng";
  if (hasDoc) return "Thuốc độc";

  if (hasNghien) {
    if (isAllCategory(matchedCategories, "Gây nghiện"))
      return "Thuốc gây nghiện";
    if (hasHuongThan || hasTienChat) return "Thuốc gây nghiện";
    if (hasUnknownActive) return "Cần check gây nghiện";
  }

  if (hasHuongThan) {
    if (isAllCategory(matchedCategories, "Hướng thần"))
      return "Thuốc hướng thần";
    if (hasTienChat) return "Thuốc hướng thần";
    if (hasUnknownActive) return "Cần check hướng thần";
  }

  if (hasTienChat) {
    if (isAllCategory(matchedCategories, "Tiền chất")) return "Thuốc tiền chất";
    if (hasUnknownActive) return "Cần check tiền chất";
  }

  return null;
};

const detect = (desc: string, groupName: string): ClassificationResult | null =>
  detectFromActiveIngredients(getActiveList(desc), groupName);

const summarizeManualClassification = (
  rawActiveIngredient: string,
): ManualClassificationSummary => {
  const activeIngredients = getNormalizedActiveList(rawActiveIngredient);
  const matchedSpecialIngredients = activeIngredients
    .map((name) => ({
      name,
      category: findCategoryForSegment(name),
    }))
    .filter(
      (
        item,
      ): item is {
        name: string;
        category: Extract<
          DrugCategory,
          "Gây nghiện" | "Hướng thần" | "Tiền chất"
        >;
      } => isHighlightedDrugCategory(item.category),
    );

  return {
    classification: detectFromActiveIngredients(activeIngredients),
    activeIngredients,
    matchedSpecialIngredients,
  };
};

// =========================
// 📤 EXPORT EXCEL
// =========================
const exportToExcel = (data: DetectedRow[]) => {
  if (!data.length) return;

  const formatted = data.map((item) => ({
    SKU: item.sku,
    "Tên sản phẩm": item.name,
    "Tên nhóm hàng hóa": item.groupName,
    "Hoạt chất": item.activeIngredient || "",
    "Mô tả": item.description || "",
    "Phân loại": item.classification || "",
  }));

  const worksheet = XLSX.utils.json_to_sheet(formatted);

  worksheet["!cols"] = [
    { wch: 15 },
    { wch: 30 },
    { wch: 28 },
    { wch: 25 },
    { wch: 50 },
    { wch: 20 },
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Drug List");

  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(blob, `drug-classification-${Date.now()}.xlsx`);
};

// =========================
// 🧠 COMPONENT
// =========================
export default function OverviewPage() {
  const [data, setData] = useState<RowData[]>([]);
  const [loading, setLoading] = useState(false);
  const [detectedList, setDetectedList] = useState<DetectedRow[]>([]);
  const [manualActiveIngredient, setManualActiveIngredient] = useState("");
  // const [tableContainerRef, { height: tableScrollY }] =
  //   useElementSize<HTMLDivElement>(0, 120);

  // const SHEET_URL =
  //   'https://docs.google.com/spreadsheets/d/1_HU9U9TYELjx0YIaBc9QLYtI5PkVddi_/gviz/tq?tqx=out:json&gid=662401770';

  // const SHEET_URL =
  //   "https://docs.google.com/spreadsheets/d/1eU2cuwBkbOdAhkaqdCXr-gh1Q6rSjjOZ/gviz/tq?tqx=out:json&gid=144805517";
  // 'https://docs.google.com/spreadsheets/d/15ANPm7Xe9laMUH1MtvAAsNc26V0oIJWQ/gviz/tq?tqx=out:json&gid=662401770';

  // 15ANPm7Xe9laMUH1MtvAAsNc26V0oIJWQ

  // useEffect(() => {
  //   const fetchData = async () => {
  //     setLoading(true);
  //     try {
  //       const res = await fetch(SHEET_URL);
  //       const text = await res.text();
  //       const json = parseGoogleSheetResponse(text);

  //       const cols = json.table.cols.map((col) => col.label ?? "");

  //       const mappedRows: RowData[] = json.table.rows.map((row) => {
  //         const obj: RowData = {};
  //         row.c.forEach((cell, index) => {
  //           obj[cols[index]] = cell?.v ?? "";
  //         });
  //         return obj;
  //       });
  //       console.log(
  //         "🚀 ~ file: index.tsx:333 ~ fetchData ~ mappedRows:",
  //         mappedRows,
  //       );
  //       const rows = mappedRows
  //         .filter((item) => getRowText(item, ["- Mã SKU", "SKU"]) !== "")
  //         .map((item) => {
  //           const description = getRowText(item, ["Mô tả", "Mô tả "]);
  //           const groupName = getRowText(item, ["Tên nhóm hàng hóa"]);

  //           return {
  //             ...item,
  //             classification: detect(description, groupName),
  //           };
  //         });

  //       console.log("🚀 ~ file: index.tsx:333 ~ fetchData ~ rows:", rows);

  //       const detected = rows
  //         .filter(
  //           (
  //             item,
  //           ): item is RowData & { classification: ClassificationResult } =>
  //             item.classification != null,
  //         )
  //         .map((item) => {
  //           const desc = getRowText(item, ["Mô tả", "Mô tả "]);
  //           const groupName = getRowText(item, ["Tên nhóm hàng hóa"]);

  //           return {
  //             sku: getRowText(item, ["- Mã SKU", "SKU"]),
  //             name: getRowText(item, [
  //               "Tên sản phẩm",
  //               "Tên hàng hóa (*)",
  //               "Tên hàng hóa",
  //             ]),
  //             groupName,
  //             description: desc,
  //             classification: item.classification,
  //             activeIngredient: getActiveIngredientText(desc),
  //           };
  //         });

  //       setData(rows);
  //       setDetectedList(detected);
  //     } catch (err) {
  //       console.error(err);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchData();
  // }, []);

  const stats = useMemo(() => {
    const result: Record<string, number> = {};
    data.forEach((item) => {
      const key = item.classification || "Bình thường";
      result[key] = (result[key] || 0) + 1;
    });
    return result;
  }, [data]);

  const manualClassification = useMemo(
    () => summarizeManualClassification(manualActiveIngredient),
    [manualActiveIngredient],
  );

  const manualClassificationLabel = manualActiveIngredient.trim()
    ? manualClassification.classification || "Bình thường"
    : "Nhập hoạt chất để phân loại";

  return (
    <Box
      sx={{
        height: "calc(100vh - 30px)",
        p: 3,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Manual classification */}
      <Paper sx={{ p: 3, mb: 2, bgcolor: "grey.50" }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h4" fontWeight={600}>
              Phân loại theo hoạt chất
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Nhập một hoặc nhiều hoạt chất, phân tách bằng dấu phẩy
            </Typography>
          </Box>

          <TextField
            value={manualActiveIngredient}
            onChange={(event) => setManualActiveIngredient(event.target.value)}
            multiline
            minRows={3}
            maxRows={6}
            placeholder="Paracetamol, Codein phosphate"
            fullWidth
            sx={{
              backgroundColor: "#fff",
            }}
          />

          {/* RESULT */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "background.paper",
            }}
          >
            {/* <Typography variant="body2" color="text.secondary">
              Kết quả
            </Typography> */}

            <Typography
              variant="h6"
              fontWeight={700}
              color={
                manualClassificationLabel === "Bình thường"
                  ? "success.main"
                  : "error.main"
              }
            >
              Kết Quả: {manualClassificationLabel}
            </Typography>
          </Paper>

          {manualClassification.matchedSpecialIngredients.length > 0 && (
            <Stack spacing={1}>
              {manualClassification.matchedSpecialIngredients.map((item) => (
                <Stack
                  key={`${item.name}-${item.category}`}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <Chip label={item.name} color="primary" size="small" />
                  <Typography color="error.main" fontWeight={600}>
                    {item.category}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
